import {
  autocompletion,
  Completion,
  CompletionContext,
  CompletionResult,
} from '@codemirror/autocomplete';

export type ExpressionVariable = {
  path: string;
  label: string;
  type: string;
};

/**
 * Creates an autocompletion extension for expression variables
 * Triggers completion when user types {{ and shows available variables
 *
 * @param variables - Array of available expression variables
 * @returns CodeMirror autocompletion extension
 */
export function expressionCompletion(variables: ExpressionVariable[]) {
  return autocompletion({
    override: [
      (context: CompletionContext): CompletionResult | null => {
        // Match {{ followed by optional path characters
        const match = context.matchBefore(/\{\{[\w.]*$/);

        // If no {{ pattern found
        if (!match) {
          // Support explicit activation (Ctrl+Space) - show all variables
          if (context.explicit) {
            return {
              from: context.pos,
              options: variables.map((v) => ({
                label: `{{${v.path}}}`,
                type: 'variable',
                detail: `${v.label} (${v.type})`,
                info: `Insert {{${v.path}}}`,
              })),
            };
          }
          return null;
        }

        // Extract the partial path after {{
        const partialPath = match.text.substring(2);

        // Filter and map variables to completion options
        const options: Completion[] = variables
          .filter((v) =>
            v.path.toLowerCase().includes(partialPath.toLowerCase()),
          )
          .map((v) => ({
            label: v.path,
            type: 'variable',
            detail: `${v.label} (${v.type})`,
            info: `Insert {{${v.path}}}`,
            apply: (view, _completion, from, to) => {
              // Insert the variable path and close with }}
              view.dispatch({
                changes: { from, to, insert: `${v.path}}}` },
              });
            },
          }));

        if (options.length === 0) return null;

        return {
          from: match.from + 2, // Start after {{
          options,
          filter: false, // We already filtered
        };
      },
    ],
    icons: true,
    addToOptions: [
      {
        render: (_completion) => {
          const el = document.createElement('span');
          el.className = 'cm-completion-variable-icon';
          el.textContent = '{{}}';
          return el;
        },
        position: 20,
      },
    ],
  });
}
